// IE Hub - Color Check Page
// Comprehensive theme color testing and preview

import { useState } from 'react';
import {
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme
} from '../ui';
import { TextField, UIButton } from '../ui';
import { 
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Palette as PaletteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

export default function CheckColor(): JSX.Element {
  const theme = useTheme();
  const [textValue, setTextValue] = useState('Sample text');

  // Color palette data
  const paletteColors = [
    { name: 'Primary', color: theme.palette.primary, usage: 'Main brand color, buttons, links' },
    { name: 'Secondary', color: theme.palette.secondary, usage: 'Accent color, highlights' },
    { name: 'Success', color: theme.palette.success, usage: 'Success states, confirmations' },
    { name: 'Warning', color: theme.palette.warning, usage: 'Warnings, cautions' },
    { name: 'Error', color: theme.palette.error, usage: 'Errors, validation failures' },
    { name: 'Info', color: theme.palette.info, usage: 'Information, tips' }
  ];

  const ColorSwatch = ({ name, color, usage }: { name: string; color: any; usage: string }) => (
    <Paper data-testid="pages-check-color-container-action-1" elevation={1} sx={{ p: 3, borderRadius: 2 }}>
      <Stack data-testid="pages-check-color-container-action-2" spacing={2}>
        <Typography data-testid="pages-check-color-text-action-1" variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {name}
        </Typography>
        
        {/* Color swatches */}
        <Stack data-testid="pages-check-color-container-action-3" direction="row" spacing={1} alignItems="center">
          <Box data-testid="pages-check-color-container-action-4"
            sx={{
              width: 48,
              height: 48,
              backgroundColor: color.main,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}
          />
          <Box data-testid="pages-check-color-container-action-5"
            sx={{
              width: 32,
              height: 32,
              backgroundColor: color.light,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}
          />
          <Box data-testid="pages-check-color-container-action-6"
            sx={{
              width: 32,
              height: 32,
              backgroundColor: color.dark,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}
          />
        </Stack>

        {/* Color values */}
        <Stack data-testid="pages-check-color-container-action-7" spacing={0.5}>
          <Typography data-testid="pages-check-color-text-action-2" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            <strong>Main:</strong> {color.main}
          </Typography>
          <Typography data-testid="pages-check-color-text-action-3" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            <strong>Light:</strong> {color.light}
          </Typography>
          <Typography data-testid="pages-check-color-text-action-4" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            <strong>Dark:</strong> {color.dark}
          </Typography>
        </Stack>

        <Typography data-testid="pages-check-color-text-action-5" variant="body2" color="text.secondary">
          {usage}
        </Typography>
      </Stack>
    </Paper>
  );

  return (
    <Container data-testid="pages-check-color-container-action-8" maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box data-testid="pages-check-color-container-action-9" sx={{ mb: 4, textAlign: 'center' }}>
        <PaletteIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography data-testid="pages-check-color-text-theme-color-check" variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          Theme Color Check
        </Typography>
        <Typography data-testid="pages-check-color-text-action-6" variant="body1" color="text.secondary">
          Comprehensive preview of all theme colors and components
        </Typography>
        <Chip data-testid="pages-check-color-chip-action" 
          label={`Client: ${(theme as any).ieHub?.client || 'default'}`}
          color="primary"
          sx={{ mt: 2 }}
        />
      </Box>

      <Stack data-testid="pages-check-color-container-action-10" spacing={4}>
        {/* Color Palette */}
        <Box data-testid="pages-check-color-container-action-11">
          <Typography data-testid="pages-check-color-text-🎨-color-palette" variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            🎨 Color Palette
          </Typography>
          <Grid container spacing={3}>
            {paletteColors.map((item) => (
              <Grid item xs={12} md={6} lg={4} key={item.name}>
                <ColorSwatch {...item} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider />

        {/* Interactive Components */}
        <Box data-testid="pages-check-color-container-action-12">
          <Typography data-testid="pages-check-color-text-🧩-interactive-components" variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            🧩 Interactive Components
          </Typography>
          
          <Grid container spacing={3}>
            {/* Text Fields */}
            <Grid item xs={12} md={6}>
              <Paper data-testid="pages-check-color-container-action-13" elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography data-testid="pages-check-color-text-text-fields" variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Text Fields
                </Typography>
                <Stack data-testid="pages-check-color-container-action-14" spacing={2}>
                  <TextField data-testid="pages-check-color-input-default-text-field"
                    label="Default TextField"
                    value={textValue}
                    onChange={(e) => setTextValue(e.target.value)}
                    helperText="Check the focus border color"
                  />
                  <TextField data-testid="pages-check-color-input-required-field"
                    label="Required Field"
                    validation={{ required: true }}
                    error
                    helperText="Error state with theme error color"
                  />
                  <TextField data-testid="pages-check-color-input-disabled-field"
                    label="Disabled Field"
                    disabled
                    value="Disabled state"
                  />
                </Stack>
              </Paper>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12} md={6}>
              <Paper data-testid="pages-check-color-container-action-15" elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography data-testid="pages-check-color-text-buttons" variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Buttons
                </Typography>
                <Stack data-testid="pages-check-color-container-action-16" spacing={2}>
                  <Stack data-testid="pages-check-color-container-action-17" direction="row" spacing={1} flexWrap="wrap">
                    <UIButton data-testid="pages-check-color-btn-primary" variant="contained" color="primary">
                      Primary
                    </UIButton>
                    <UIButton data-testid="pages-check-color-btn-secondary" variant="contained" color="secondary">
                      Secondary
                    </UIButton>
                    <UIButton data-testid="pages-check-color-btn-outlined" variant="outlined" color="primary">
                      Outlined
                    </UIButton>
                  </Stack>
                  <Stack data-testid="pages-check-color-container-action-18" direction="row" spacing={1} flexWrap="wrap">
                    <UIButton data-testid="pages-check-color-btn-success" variant="contained" color="success">
                      Success
                    </UIButton>
                    <UIButton data-testid="pages-check-color-btn-warning" variant="contained" color="warning">
                      Warning
                    </UIButton>
                    <UIButton data-testid="pages-check-color-btn-error" variant="contained" color="error">
                      Error
                    </UIButton>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Status Indicators */}
            <Grid item xs={12} md={6}>
              <Paper data-testid="pages-check-color-container-action-19" elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography data-testid="pages-check-color-text-status-indicators" variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Status Indicators
                </Typography>
                <Stack data-testid="pages-check-color-container-action-20" spacing={2}>
                  <Stack data-testid="pages-check-color-container-action-21" direction="row" spacing={1} alignItems="center">
                    <CheckIcon color="success" />
                    <Typography data-testid="pages-check-color-text-success---operation-completed">Success - Operation completed</Typography>
                  </Stack>
                  <Stack data-testid="pages-check-color-container-action-22" direction="row" spacing={1} alignItems="center">
                    <WarningIcon color="warning" />
                    <Typography data-testid="pages-check-color-text-warning---attention-required">Warning - Attention required</Typography>
                  </Stack>
                  <Stack data-testid="pages-check-color-container-action-23" direction="row" spacing={1} alignItems="center">
                    <ErrorIcon color="error" />
                    <Typography data-testid="pages-check-color-text-error---something-went-wrong">Error - Something went wrong</Typography>
                  </Stack>
                  <Stack data-testid="pages-check-color-container-action-24" direction="row" spacing={1} alignItems="center">
                    <InfoIcon color="info" />
                    <Typography data-testid="pages-check-color-text-info---additional-information">Info - Additional information</Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Progress Indicators */}
            <Grid item xs={12} md={6}>
              <Paper data-testid="pages-check-color-container-action-25" elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography data-testid="pages-check-color-text-progress-indicators" variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Progress Indicators
                </Typography>
                <Stack data-testid="pages-check-color-container-action-26" spacing={2}>
                  {/* Progress bar like in OnBoarding */}
                  <Stack data-testid="pages-check-color-container-action-27" direction="row" spacing={1} alignItems="center">
                    <Box data-testid="pages-check-color-container-action-28" sx={{ width: 16, height: 8, bgcolor: 'primary.main', borderRadius: 999 }} />
                    <Typography data-testid="pages-check-color-text-primary-progress" variant="body2">Primary Progress</Typography>
                  </Stack>
                  <Stack data-testid="pages-check-color-container-action-29" direction="row" spacing={1} alignItems="center">
                    <Box data-testid="pages-check-color-container-action-30" sx={{ width: 16, height: 8, bgcolor: 'secondary.main', borderRadius: 999 }} />
                    <Typography data-testid="pages-check-color-text-secondary-progress" variant="body2">Secondary Progress</Typography>
                  </Stack>
                  <Stack data-testid="pages-check-color-container-action-31" direction="row" spacing={1} alignItems="center">
                    <Box data-testid="pages-check-color-container-action-32" sx={{ width: 16, height: 8, bgcolor: 'success.main', borderRadius: 999 }} />
                    <Typography data-testid="pages-check-color-text-success-progress" variant="body2">Success Progress</Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Typography Showcase */}
        <Box data-testid="pages-check-color-container-action-33">
          <Typography data-testid="pages-check-color-text-📝-typography-scale" variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            📝 Typography Scale
          </Typography>
          <Paper data-testid="pages-check-color-container-action-34" elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Stack data-testid="pages-check-color-container-action-35" spacing={2}>
              <Typography data-testid="pages-check-color-text-h1-heading" variant="h1" color="primary.main">H1 Heading</Typography>
              <Typography data-testid="pages-check-color-text-h2-heading" variant="h2" color="text.primary">H2 Heading</Typography>
              <Typography data-testid="pages-check-color-text-h3-heading" variant="h3" color="text.primary">H3 Heading</Typography>
              <Typography data-testid="pages-check-color-text-h4-heading" variant="h4" color="text.primary">H4 Heading</Typography>
              <Typography data-testid="pages-check-color-text-h5-heading" variant="h5" color="text.primary">H5 Heading</Typography>
              <Typography data-testid="pages-check-color-text-h6-heading" variant="h6" color="text.primary">H6 Heading</Typography>
              <Typography data-testid="pages-check-color-text-action-7" variant="body1" color="text.primary">
                Body 1 - Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              </Typography>
              <Typography data-testid="pages-check-color-text-action-8" variant="body2" color="text.secondary">
                Body 2 - Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
              </Typography>
              <Typography data-testid="pages-check-color-text-caption---small-text-for-additional-information" variant="caption" color="text.secondary">
                Caption - Small text for additional information
              </Typography>
            </Stack>
          </Paper>
        </Box>

        {/* Theme Info */}
        <Box data-testid="pages-check-color-container-action-36">
          <Typography data-testid="pages-check-color-text-🔧-theme-configuration" variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            🔧 Theme Configuration
          </Typography>
          <Paper data-testid="pages-check-color-container-action-37" elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography data-testid="pages-check-color-text-current-theme" variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Current Theme
                </Typography>
                <Stack data-testid="pages-check-color-container-action-38" spacing={1}>
                  <Typography data-testid="pages-check-color-text-action-9" variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Client:</strong> {(theme as any).ieHub?.client || 'default'}
                  </Typography>
                  <Typography data-testid="pages-check-color-text-action-10" variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Version:</strong> {(theme as any).ieHub?.version || 'N/A'}
                  </Typography>
                  <Typography data-testid="pages-check-color-text-px" variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Border Radius:</strong> {theme.shape.borderRadius}px
                  </Typography>
                  <Typography data-testid="pages-check-color-text-action-11" variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Spacing Unit:</strong> {theme.spacing(1)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography data-testid="pages-check-color-text-brand-colors" variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Brand Colors
                </Typography>
                <Stack data-testid="pages-check-color-container-action-39" spacing={1}>
                  <Typography data-testid="pages-check-color-text-action-12" variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Primary:</strong> {theme.palette.primary.main}
                  </Typography>
                  <Typography data-testid="pages-check-color-text-action-13" variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Secondary:</strong> {theme.palette.secondary.main}
                  </Typography>
                  <Typography data-testid="pages-check-color-text-action-14" variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Background:</strong> {theme.palette.background.default}
                  </Typography>
                  <Typography data-testid="pages-check-color-text-action-15" variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Text Primary:</strong> {theme.palette.text.primary}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Stack>
    </Container>
  );
}
